// Local building frames and the primitive shapes the town is assembled from (walls, trim
// boxes, beams, cylinders, cards), all writing into a KitGeo with atlas tiles.
//
// A Frame is a right-handed local basis on the ground plane: U along a facade (to the right
// as seen from the street), Y up, W out of the facade toward the viewer. P(u, y, w) is world.

import { KitGeo, RGB, TILE_M } from './town_kit';
import type { TileName } from './town_atlas';

export type P3 = number[];

export class Frame {
  constructor(public ox: number, public oz: number, public ux: number, public uz: number) {}
  /** W = U x Y */
  get wx() { return -this.uz; }
  get wz() { return this.ux; }
  p(u: number, y: number, w: number): P3 {
    return [this.ox + this.ux * u + this.wx * w, y, this.oz + this.uz * u + this.wz * w];
  }
  /** a frame for the same basis rotated by `a` radians about Y, re-centred at local (u, w) */
  sub(u: number, w: number, a = 0): Frame {
    const c = Math.cos(a), s = Math.sin(a);
    // rotate U toward W
    const nux = this.ux * c + this.wx * s, nuz = this.uz * c + this.wz * s;
    const o = this.p(u, 0, w);
    return new Frame(o[0], o[2], nux, nuz);
  }
}

export const WORLD = new Frame(0, 0, 1, 0); // U = +x, W = +z

/** Ambient-occlusion profile for a wall at height y (ground contact + eave shadow). */
export type AoFn = (y: number) => number;
export const NO_AO: AoFn = () => 1;

/** A vertical wall quad in the frame's U/Y plane at depth w, facing +W. Repeating tile in metres. */
export function wall(g: KitGeo, F: Frame, u0: number, u1: number, y0: number, y1: number, w: number, tile: TileName, col: RGB, ao: AoFn = NO_AO, uOff = 0, cuts?: number[]) {
  const M = TILE_M[tile] ?? 1;
  const ys = [y0];
  if (cuts) for (const c of cuts) if (c > y0 + 1e-3 && c < y1 - 1e-3) ys.push(c);
  ys.push(y1);
  ys.sort((a, b) => a - b);
  for (let i = 0; i + 1 < ys.length; i++) {
    const ya = ys[i], yb = ys[i + 1];
    g.quad(F.p(u0, ya, w), F.p(u1, ya, w), F.p(u1, yb, w), F.p(u0, yb, w),
      [(u0 + uOff) / M, ya / M], [(u1 + uOff) / M, ya / M], [(u1 + uOff) / M, yb / M], [(u0 + uOff) / M, yb / M],
      tile, col, [ao(ya), ao(ya), ao(yb), ao(yb)]);
  }
}

/** A decal quad (0..1 uv) in the frame plane at depth w facing +W. */
export function decal(g: KitGeo, F: Frame, u0: number, u1: number, y0: number, y1: number, w: number, tile: TileName, col?: RGB, ao?: number, cu0 = 0, cu1 = 1, cv0 = 0, cv1 = 1) {
  const a = ao ?? 1;
  g.decal(F.p(u0, y0, w), F.p(u1, y0, w), F.p(u1, y1, w), F.p(u0, y1, w), tile, col, [a, a, a, a], cu0, cu1, cv0, cv1);
}

export interface BoxOpts {
  /** faces to skip: 'f'ront(+W) 'b'ack 'r'ight(+U) 'l'eft 't'op 'd'own */
  skip?: string;
  /** decal tile for the front face instead of the repeating tile */
  front?: TileName;
  ao?: AoFn;
  /** shade the top a little brighter / sides */
  kTop?: number;
}

/** Box in a frame: u0..u1, y0..y1, w0..w1. Repeating tile mapped in metres on every face. */
export function box(g: KitGeo, F: Frame, u0: number, u1: number, y0: number, y1: number, w0: number, w1: number, tile: TileName, col: RGB, o: BoxOpts = {}) {
  const M = TILE_M[tile] ?? 1;
  const sk = o.skip ?? '';
  const ao = o.ao ?? NO_AO;
  const A = [ao(y0), ao(y0), ao(y1), ao(y1)];
  if (!sk.includes('f')) {
    if (o.front) g.decal(F.p(u0, y0, w1), F.p(u1, y0, w1), F.p(u1, y1, w1), F.p(u0, y1, w1), o.front, [1, 1, 1], A);
    else g.quad(F.p(u0, y0, w1), F.p(u1, y0, w1), F.p(u1, y1, w1), F.p(u0, y1, w1), [u0 / M, y0 / M], [u1 / M, y0 / M], [u1 / M, y1 / M], [u0 / M, y1 / M], tile, col, A);
  }
  if (!sk.includes('b')) g.quad(F.p(u1, y0, w0), F.p(u0, y0, w0), F.p(u0, y1, w0), F.p(u1, y1, w0), [-u1 / M, y0 / M], [-u0 / M, y0 / M], [-u0 / M, y1 / M], [-u1 / M, y1 / M], tile, col, A);
  if (!sk.includes('r')) g.quad(F.p(u1, y0, w1), F.p(u1, y0, w0), F.p(u1, y1, w0), F.p(u1, y1, w1), [-w1 / M, y0 / M], [-w0 / M, y0 / M], [-w0 / M, y1 / M], [-w1 / M, y1 / M], tile, col, A);
  if (!sk.includes('l')) g.quad(F.p(u0, y0, w0), F.p(u0, y0, w1), F.p(u0, y1, w1), F.p(u0, y1, w0), [w0 / M, y0 / M], [w1 / M, y0 / M], [w1 / M, y1 / M], [w0 / M, y1 / M], tile, col, A);
  const kt = o.kTop ?? 1;
  const ct: RGB = kt === 1 ? col : [col[0] * kt, col[1] * kt, col[2] * kt];
  const at = ao(y1), ab = ao(y0);
  if (!sk.includes('t')) g.quad(F.p(u0, y1, w1), F.p(u1, y1, w1), F.p(u1, y1, w0), F.p(u0, y1, w0), [u0 / M, w1 / M], [u1 / M, w1 / M], [u1 / M, w0 / M], [u0 / M, w0 / M], tile, ct, [at, at, at, at]);
  if (!sk.includes('d')) g.quad(F.p(u0, y0, w0), F.p(u1, y0, w0), F.p(u1, y0, w1), F.p(u0, y0, w1), [u0 / M, w0 / M], [u1 / M, w0 / M], [u1 / M, w1 / M], [u0 / M, w1 / M], tile, col, [ab * 0.8, ab * 0.8, ab * 0.8, ab * 0.8]);
}

/** Square-section beam between two world points (posts, braces, crossarms, rails). */
export function beam(g: KitGeo, a: P3, b: P3, wdt: number, hgt: number, tile: TileName, col: RGB, ao = 1) {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const L = Math.hypot(dx, dy, dz);
  if (L < 1e-6) return;
  const fx = dx / L, fy = dy / L, fz = dz / L;
  // side = f x up (or x when vertical), up2 = side x f
  let sx = fy * 0 - fz * 1, sy = fz * 0 - fx * 0, sz = fx * 1 - fy * 0;
  let sl = Math.hypot(sx, sy, sz);
  if (sl < 1e-4) { sx = 1; sy = 0; sz = 0; sl = 1; }
  sx /= sl; sy /= sl; sz /= sl;
  const ux = sy * fz - sz * fy, uy = sz * fx - sx * fz, uz = sx * fy - sy * fx;
  const hw = wdt / 2, hh = hgt / 2;
  const M = TILE_M[tile] ?? 1;
  const c = (p: P3, s: number, u: number): P3 => [p[0] + sx * s * hw + ux * u * hh, p[1] + sy * s * hw + uy * u * hh, p[2] + sz * s * hw + uz * u * hh];
  const corners: [number, number][] = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
  const per = [wdt, hgt, wdt, hgt];
  let acc = 0;
  const A = [ao, ao, ao, ao];
  for (let i = 0; i < 4; i++) {
    const [s0, u0] = corners[i], [s1, u1] = corners[(i + 1) % 4];
    const v0 = acc / M, v1 = (acc + per[i]) / M;
    g.quad(c(a, s1, u1), c(a, s0, u0), c(b, s0, u0), c(b, s1, u1), [v0, 0], [v1, 0], [v1, L / M], [v0, L / M], tile, col, A);
    acc += per[i];
  }
  // end caps
  g.quad(c(b, 1, -1), c(b, -1, -1), c(b, -1, 1), c(b, 1, 1), [0, 0], [wdt / M, 0], [wdt / M, hgt / M], [0, hgt / M], tile, col, A);
  g.quad(c(a, 1, 1), c(a, -1, 1), c(a, -1, -1), c(a, 1, -1), [0, 0], [wdt / M, 0], [wdt / M, hgt / M], [0, hgt / M], tile, col, A);
}

/** Vertical (optionally tapered) cylinder; `lean` shifts the top by (dx, dz). */
export function cyl(g: KitGeo, cx: number, cz: number, y0: number, y1: number, r0: number, r1: number, n: number, tile: TileName, col: RGB,
  o: { top?: TileName | null; topCol?: RGB; bottom?: boolean; dx?: number; dz?: number; ao0?: number; ao1?: number; a0?: number; decalU?: boolean } = {}) {
  const M = TILE_M[tile] ?? 1;
  const dx = o.dx ?? 0, dz = o.dz ?? 0, a0 = o.a0 ?? 0;
  const ao0 = o.ao0 ?? 1, ao1 = o.ao1 ?? 1;
  const circ = 2 * Math.PI * Math.max(r0, r1);
  for (let i = 0; i < n; i++) {
    const a = a0 + (i / n) * Math.PI * 2, b = a0 + ((i + 1) / n) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a), cb = Math.cos(b), sb = Math.sin(b);
    // CCW from outside: going around with increasing angle in x/z means clockwise seen from +y,
    // so emit (a bottom, b bottom, b top, a top) with b before a
    const u0 = o.decalU ? i / n : (circ * i) / n / M, u1 = o.decalU ? (i + 1) / n : (circ * (i + 1)) / n / M;
    const v0 = o.decalU ? 0 : y0 / M, v1 = o.decalU ? 1 : y1 / M;
    g.quad([cx + cb * r0, y0, cz + sb * r0], [cx + ca * r0, y0, cz + sa * r0], [cx + dx + ca * r1, y1, cz + dz + sa * r1], [cx + dx + cb * r1, y1, cz + dz + sb * r1],
      [u1, v0], [u0, v0], [u0, v1], [u1, v1], tile, col, [ao0, ao0, ao1, ao1]);
  }
  if (o.top !== null) {
    const tt = o.top ?? tile;
    const tc = o.topCol ?? col;
    const Mt = TILE_M[tt] ?? 1;
    for (let i = 0; i < n; i++) {
      const a = a0 + (i / n) * Math.PI * 2, b = a0 + ((i + 1) / n) * Math.PI * 2;
      const p0 = [cx + dx, y1, cz + dz], pa = [cx + dx + Math.cos(a) * r1, y1, cz + dz + Math.sin(a) * r1], pb = [cx + dx + Math.cos(b) * r1, y1, cz + dz + Math.sin(b) * r1];
      g.tri([p0, pb, pa], [[p0[0] / Mt, p0[2] / Mt], [pb[0] / Mt, pb[2] / Mt], [pa[0] / Mt, pa[2] / Mt]], tt, tc, [ao1, ao1, ao1]);
    }
  }
}

/** Disc in a frame plane (round signs, wheels, portholes) facing +W, decal mapped. */
export function disc(g: KitGeo, F: Frame, cu: number, cy: number, w: number, r: number, n: number, tile: TileName, col: RGB = [1, 1, 1], flipU = false) {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2, b = ((i + 1) / n) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a), cb = Math.cos(b), sb = Math.sin(b);
    const uu = (c: number) => (flipU ? 0.5 - c * 0.5 : 0.5 + c * 0.5);
    g.tri([F.p(cu, cy, w), F.p(cu + ca * r, cy + sa * r, w), F.p(cu + cb * r, cy + sb * r, w)],
      [[0.5, 0.5], [uu(ca), 0.5 + sa * 0.5], [uu(cb), 0.5 + sb * 0.5]], tile, col);
  }
}

/** Two crossed vertical cards (foliage, lanterns) centred at world (x, z). */
export function crossCards(g: KitGeo, x: number, y0: number, z: number, w: number, h: number, tile: TileName, col: RGB = [1, 1, 1], a0 = 0.4, cu0 = 0, cu1 = 1) {
  for (let k = 0; k < 2; k++) {
    const a = a0 + k * Math.PI / 2;
    const F = new Frame(x, z, Math.cos(a), Math.sin(a));
    decal(g, F, -w / 2, w / 2, y0, y0 + h, 0, tile, col, 1, cu0, cu1);
  }
}

/** A strip card hung along a sagging curve (bunting, laundry): u repeats along the length. */
export function hangStrip(g: KitGeo, a: P3, b: P3, sag: number, drop: number, tile: TileName, tileLen: number, col: RGB = [1, 1, 1], segs = 12) {
  let s0 = 0;
  let prev = a;
  for (let i = 1; i <= segs; i++) {
    const t = i / segs;
    const p = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t - sag * 4 * t * (1 - t), a[2] + (b[2] - a[2]) * t];
    const L = Math.hypot(p[0] - prev[0], p[1] - prev[1], p[2] - prev[2]);
    const s1 = s0 + L / tileLen;
    g.quad([prev[0], prev[1] - drop, prev[2]], [p[0], p[1] - drop, p[2]], p, prev, [s0, 0], [s1, 0], [s1, 1], [s0, 1], tile, col);
    prev = p; s0 = s1;
  }
}

export function catenary(out: number[], a: P3, b: P3, sag: number, segs = 14) {
  for (let i = 0; i < segs; i++) {
    for (const t of [i / segs, (i + 1) / segs]) {
      out.push(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t - sag * 4 * t * (1 - t), a[2] + (b[2] - a[2]) * t);
    }
  }
}

export type Hole = [number, number, number, number]; // u0, u1, y0, y1

/** A wall like `wall()` but with rectangular openings cut out (windows and doors get depth). */
export function wallHoles(g: KitGeo, F: Frame, u0: number, u1: number, y0: number, y1: number, w: number, tile: TileName, col: RGB, ao: AoFn, holes: Hole[], cuts?: number[]) {
  const hs = holes.filter((h) => h[1] > u0 && h[0] < u1).map((h) => [Math.max(u0, h[0]), Math.min(u1, h[1]), Math.max(y0, h[2]), Math.min(y1, h[3])] as Hole);
  if (!hs.length) { wall(g, F, u0, u1, y0, y1, w, tile, col, ao, 0, cuts); return; }
  const us = [u0, u1];
  for (const h of hs) us.push(h[0], h[1]);
  us.sort((a, b) => a - b);
  for (let i = 0; i + 1 < us.length; i++) {
    const a = us[i], b = us[i + 1];
    if (b - a < 1e-4) continue;
    const m = (a + b) / 2;
    const ys = hs.filter((h) => h[0] <= m && h[1] >= m).map((h) => [h[2], h[3]]).sort((p, q) => p[0] - q[0]);
    let y = y0;
    for (const [ha, hb] of ys) {
      if (ha > y + 1e-4) wall(g, F, a, b, y, ha, w, tile, col, ao, 0, cuts);
      y = Math.max(y, hb);
    }
    if (y1 > y + 1e-4) wall(g, F, a, b, y, y1, w, tile, col, ao, 0, cuts);
  }
}

/** The four inner faces of an opening u0..u1, y0..y1 from the wall plane back to -depth. */
export function reveal(g: KitGeo, F: Frame, u0: number, u1: number, y0: number, y1: number, depth: number, tile: TileName, col: RGB) {
  const d = -depth;
  const M = TILE_M[tile] ?? 1;
  const dk: RGB = [col[0] * 0.8, col[1] * 0.8, col[2] * 0.8];
  // top (faces down)
  g.quad(F.p(u0, y1, d), F.p(u1, y1, d), F.p(u1, y1, 0), F.p(u0, y1, 0), [u0 / M, 0], [u1 / M, 0], [u1 / M, depth / M], [u0 / M, depth / M], tile, dk, [0.4, 0.4, 0.62, 0.62]);
  // bottom (faces up)
  g.quad(F.p(u0, y0, 0), F.p(u1, y0, 0), F.p(u1, y0, d), F.p(u0, y0, d), [u0 / M, 0], [u1 / M, 0], [u1 / M, depth / M], [u0 / M, depth / M], tile, col, [1, 1, 0.7, 0.7]);
  // left side (faces +U)
  g.quad(F.p(u0, y0, 0), F.p(u0, y0, d), F.p(u0, y1, d), F.p(u0, y1, 0), [0, y0 / M], [depth / M, y0 / M], [depth / M, y1 / M], [0, y1 / M], tile, col, [0.85, 0.55, 0.55, 0.85]);
  // right side (faces -U)
  g.quad(F.p(u1, y0, d), F.p(u1, y0, 0), F.p(u1, y1, 0), F.p(u1, y1, d), [0, y0 / M], [depth / M, y0 / M], [depth / M, y1 / M], [0, y1 / M], tile, col, [0.55, 0.85, 0.85, 0.55]);
}
